import type {Component, ComponentDefinition, CssRule, Editor} from 'grapesjs';
import type {BirthdayFormat, FieldMeta} from '@FormMakerReact/form-maker.types';
import {PMW_CUSTOM_INPUT} from '@FormMakerReact/form-maker.types';
import {POSTERMYWALL_FIRST_NAME_BOX_TYPE} from '@FormMakerReact/components/postermywall-first-name-box/postermywall-first-name-box';
import {POSTERMYWALL_LAST_NAME_BOX_TYPE} from '@FormMakerReact/components/postermywall-last-name-box/postermywall-last-name-box';
import {POSTERMYWALL_BIRTHDAY_BOX_TYPE} from '@FormMakerReact/components/postermywall-birthday-box/postermywall-birthday-box';
import {POSTERMYWALL_ADDRESS_BOX_TYPE} from '@FormMakerReact/components/postermywall-address-box/postermywall-address-box';
import {POSTERMYWALL_PHONE_BOX_TYPE} from '@FormMakerReact/components/postermywall-phone-box/postermywall-phone-box';
import {POSTERMYWALL_CUSTOM_BOX_TYPE_PREFIX, buildCustomBoxType} from '@FormMakerReact/components/postermywall-custom-box/postermywall-custom-box';
import {POSTERMYWALL_EMAIL_FORM_TYPE} from '@FormMakerReact/components/postermywall-email-form/postermywall-email-form';
import {POSTERMYWALL_FORM_BACKGROUND_TYPE, SIGNUP_CONTAINER_ID} from '@FormMakerReact/components/postermywall-form-background/postermywall-form-background';
import {POSTERMYWALL_FORMS_WRAPPER_TYPE} from '@FormMakerReact/components/postermywall-forms-wrapper/postermywall-forms-wrapper';
import {POSTERMYWALL_SIGNUP_FORM_TYPE} from '@FormMakerReact/components/postermywall-signup-form/postermywall-signup-form';
import {POSTERMYWALL_FORM_TEXT_TYPE} from '@FormMakerReact/components/postermywall-form-text/postermywall-form-text';
import {POSTERMYWALL_HEADING_TEXT_TYPE} from '@FormMakerReact/components/postermywall-heading-text/postermywall-heading-text';
import {POSTERMYWALL_DESCRIPTION_TEXT_TYPE} from '@FormMakerReact/components/postermywall-description-text/postermywall-description-text';
import {POSTERMYWALL_SUBMIT_BUTTON_CONTAINER_TYPE} from '@FormMakerReact/components/postermywall-submit-button-container/postermywall-submit-button-container';
import {POSTERMYWALL_POLICY_TYPE} from '@FormMakerReact/components/postermywall-policy/postermywall-policy';
import {POSTERMYWALL_ERROR_BOX_TYPE} from '@FormMakerReact/components/postermywall-error-box/postermywall-error-box';
import {STORE_OPTS_MODEL_KEY} from '@FormMakerReact/grapesjs-form-maker-plugin/src/form-maker-storage';
import {POSTERMYWALL_DAY_INPUT_TYPE} from '@FormMakerReact/components/postermywall-day-input/postermywall-day-input';
import {POSTERMYWALL_MONTH_INPUT_TYPE} from '@FormMakerReact/components/postermywall-month-input/postermywall-month-input';
import {POSTERMYWALL_YEAR_INPUT_TYPE} from '@FormMakerReact/components/postermywall-year-input/postermywall-year-input';
import {POSTERMYWALL_FIRST_NAME_INPUT_TYPE} from '@FormMakerReact/components/postermywall-first-name-input/postermywall-first-name-input';
import {POSTERMYWALL_LAST_NAME_INPUT_TYPE} from '@FormMakerReact/components/postermywall-last-name-input/postermywall-last-name-input';
import {POSTERMYWALL_PHONE_INPUT_TYPE} from '@FormMakerReact/components/postermywall-phone-input/postermywall-phone-input';
import {POSTERMYWALL_STREET_INPUT_TYPE} from '@FormMakerReact/components/postermywall-street-input/postermywall-street-input';
import {POSTERMYWALL_CITY_INPUT_TYPE} from '@FormMakerReact/components/postermywall-city-input/postermywall-city-input';
import {POSTERMYWALL_STATE_INPUT_TYPE} from '@FormMakerReact/components/postermywall-state-input/postermywall-state-input';
import {POSTERMYWALL_COUNTRY_INPUT_TYPE} from '@FormMakerReact/components/postermywall-country-input/postermywall-country-input';
import {FORM_ASTERISK_STYLES} from '@FormMakerReact/components/component-styles';
import {MAX_AUDIENCE_CUSTOM_FIELDS_COUNT} from '@Panels/add-mailing-list-panel/add-mailing-list-panel.types';
import {escapeHTML} from '@Utils/string.util';

// Email is always-on and never appears in this list.
export const TOGGLEABLE_BOX_TYPES = [
  POSTERMYWALL_FIRST_NAME_BOX_TYPE,
  POSTERMYWALL_LAST_NAME_BOX_TYPE,
  POSTERMYWALL_BIRTHDAY_BOX_TYPE,
  POSTERMYWALL_ADDRESS_BOX_TYPE,
  POSTERMYWALL_PHONE_BOX_TYPE,
] as const;

// Mirrors server-side MAXIMUM_NUMBER_OF_USER_CUSTOM_FIELDS_FOR_MAILING_LIST.
export const MAX_CUSTOM_FIELDS = MAX_AUDIENCE_CUSTOM_FIELDS_COUNT;

export function isFieldEnabled(editor: Editor, boxType: string): boolean {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return false;
  }
  // findType() works on the model tree, before or after rendering.
  return wrapper.findType(boxType).length > 0;
}

// Mutates the canvas only; fieldsMeta is recomputed at save-time.
export function toggleField(editor: Editor, boxType: string): void {
  const enabled = isFieldEnabled(editor, boxType);
  if (enabled) {
    removeField(editor, boxType);
  } else {
    addField(editor, boxType);
  }
}

function removeField(editor: Editor, boxType: string): void {
  const wrapper = editor.getWrapper();
  if (wrapper) {
    wrapper.findType(boxType).forEach((comp: Component) => {
      comp.remove();
    });
  }
}

export function removeCustomField(editor: Editor, customFieldIndex: number): void {
  const boxType = buildCustomBoxType(customFieldIndex);
  removeField(editor, boxType);
}

// Caller must ensure the index isn't already on canvas — this appends unconditionally.
export function restoreCustomField(editor: Editor, customFieldIndex: number, fieldsMeta: readonly FieldMeta[]): void {
  const boxType = buildCustomBoxType(customFieldIndex);
  addField(editor, boxType);
  const entry = fieldsMeta.find((f) => f.component === PMW_CUSTOM_INPUT && f.customFieldIndex === customFieldIndex);
  if (entry) {
    updateCustomFieldLabelOnCanvas(editor, customFieldIndex, entry.label);
    if (entry.required !== undefined) {
      updateFieldRequired(editor, boxType, entry.required);
    }
  }
}

export function moveBoxInForm(editor: Editor, boxType: string, targetChildIndex: number): void {
  const emailForm = findSignupEmailForm(editor);
  if (!emailForm) {
    return;
  }
  const collection = emailForm.components();
  const box = collection.models.find((c) => c.get('type') === boxType);
  if (!box) {
    return;
  }
  const currentIndex = collection.indexOf(box);
  if (currentIndex === -1 || currentIndex === targetChildIndex) {
    return;
  }
  collection.remove(box);
  collection.add(box, {at: targetChildIndex});
}

export function stripBogusRequiredAttributes(editor: Editor): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return;
  }
  walkComponentTree(wrapper, (component) => {
    if (component.get('tagName') !== 'input') {
      return;
    }
    const attrs = component.getAttributes() as Record<string, unknown>;
    if (attrs.required !== 'false') {
      return;
    }
    const {required: _required, ...rest} = attrs;
    component.setAttributes(rest);
  });
}

function walkComponentTree(component: Component, visit: (c: Component) => void): void {
  visit(component);
  component.components().forEach((child) => {
    walkComponentTree(child, visit);
  });
}

export function ensureSubmitButtonContainerBoxSizing(editor: Editor): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return;
  }
  walkComponentTree(wrapper, (component) => {
    if (!isSubmitButtonContainer(component)) {
      return;
    }
    const style = component.getStyle() as Record<string, string | undefined>;
    if (style['box-sizing'] === 'border-box') {
      return;
    }
    component.addStyle({'box-sizing': 'border-box'});
  });
}

function isSubmitButtonContainer(component: Component): boolean {
  if (component.get('type') === POSTERMYWALL_SUBMIT_BUTTON_CONTAINER_TYPE) {
    return true;
  }
  const classes = component.get('classes') as {pluck?: (key: string) => string[]} | string[] | undefined;
  const classNames = Array.isArray(classes) ? classes : (classes?.pluck?.('name') ?? []);
  if (classNames.includes(POSTERMYWALL_SUBMIT_BUTTON_CONTAINER_TYPE)) {
    return true;
  }
  const attrs = component.getAttributes() as Record<string, unknown>;
  return attrs['data-gjs-type'] === POSTERMYWALL_SUBMIT_BUTTON_CONTAINER_TYPE || attrs['data-pmw-type'] === POSTERMYWALL_SUBMIT_BUTTON_CONTAINER_TYPE;
}

export function stripHeadingTextMinHeight(editor: Editor): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return;
  }
  wrapper.findType(POSTERMYWALL_HEADING_TEXT_TYPE).forEach((heading: Component) => {
    const style = heading.getStyle() as Record<string, string | undefined>;
    if (style['min-height'] === undefined) {
      return;
    }
    heading.removeStyle('min-height');
  });
}

/**
 * Forms created via the createDynamicForm flow store per-component styles as
 * id-selector CSS rules in the GrapesJS Css composer (e.g.
 * `#iirw { color: ... }`) instead of as inline styles on the component model.
 * Two regressions follow from that schema mismatch:
 *   1. The font selector's `forEachDescendantWithFontFamily` reads
 *      `component.getStyle()` which only sees inline-on-model styles, so it
 *      skips any descendant whose font-family lives in a CSS rule.
 *   2. Drag-reorder remove-and-readds the box; the rendered styles disappear
 *      after the move because the rule path stops matching cleanly.
 *
 * Merging the rule styles into the component model on load aligns dynamic
 * forms with the inline-style schema every other code path expects, after
 * which both bugs go away and the next autosave persists the corrected shape.
 *
 * Two cases the migration cannot silently transform:
 *   - State-bearing id rules (`#id:hover`, `#id.foo`) — pseudo-classes and
 *     compound selectors don't have an inline-style equivalent. None exist in
 *     current data, but a future variant could introduce them; we surface
 *     them via console.warn so the gap is visible instead of silently broken.
 *   - GrapesJS internally re-creating an id rule in response to `addStyle`
 *     under some configs. We verify post-removal that no `#${id}` rule
 *     survived; if one does, a warn fires so a future GrapesJS upgrade that
 *     changes the sync behavior is caught at runtime.
 */
export function migrateCssRulesToInlineStyles(editor: Editor): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return;
  }
  const cssc = editor.Css;
  const componentsById = collectComponentsById(wrapper);
  const orphanedStateRules = inlineRulesIntoComponents(cssc, componentsById);
  warnIfOrphanedStateRules(orphanedStateRules);
  warnIfRulesSurvived(cssc, componentsById);
}

interface IdSelectorMatch {
  id: string;
  suffix: string;
}

/**
 * Snapshots the css composer's current rule list (we mutate via remove during the
 * loop) and routes each rule through one of three branches based on its selector:
 *  - pure id selector matching a known component → merge into the component model
 *    via addStyle and drop the rule from the composer.
 *  - state-bearing id selector (e.g. `#id:hover`, `#id.foo`) matching a known
 *    component → push into the orphan list to be warned about.
 *  - anything else (page-level rules, unknown ids) → skip.
 *
 * Returns the orphan-state-rule selectors so the caller can surface them.
 */
function inlineRulesIntoComponents(cssc: Editor['Css'], componentsById: ReadonlyMap<string, Component>): string[] {
  const orphanedStateRules: string[] = [];
  const ruleSnapshot = cssc.getAll().models.slice();
  ruleSnapshot.forEach((rule) => {
    const fullSelector = rule.getSelectors().getFullString();
    const match = parseIdSelector(fullSelector);
    if (!match) {
      return;
    }
    const component = componentsById.get(match.id);
    if (!component) {
      return;
    }
    if (match.suffix.length > 0) {
      orphanedStateRules.push(fullSelector);
      return;
    }
    moveRuleStyleToComponent(rule, component, cssc);
  });
  return orphanedStateRules;
}

function parseIdSelector(fullSelector: string): IdSelectorMatch | null {
  const match = /^#([a-zA-Z0-9_-]+)(.*)$/.exec(fullSelector);
  if (!match) {
    return null;
  }
  return {id: match[1], suffix: match[2]};
}

function moveRuleStyleToComponent(rule: CssRule, component: Component, cssc: Editor['Css']): void {
  const ruleStyle = rule.getStyle() as Record<string, string>;
  if (Object.keys(ruleStyle).length > 0) {
    component.addStyle(ruleStyle);
  }
  cssc.remove(rule);
}

function warnIfOrphanedStateRules(orphanedSelectors: readonly string[]): void {
  if (orphanedSelectors.length === 0) {
    return;
  }
  console.warn(`[form-maker] State-bearing id rules left in CSS composer (cannot be inlined): ${orphanedSelectors.join(', ')}`);
}

function warnIfRulesSurvived(cssc: Editor['Css'], componentsById: ReadonlyMap<string, Component>): void {
  componentsById.forEach((_component, id) => {
    if (cssc.getRules(`#${id}`).length > 0) {
      console.warn(`[form-maker] CSS rule for #${id} survived migration — GrapesJS sync may be reviving it`);
    }
  });
}

function collectComponentsById(root: Component): Map<string, Component> {
  const map = new Map<string, Component>();
  walkComponentTree(root, (component) => {
    const id = component.getId();
    if (id) {
      map.set(id, component);
    }
  });
  return map;
}

export function ensureWrapperHorizontalPadding(editor: Editor): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return;
  }
  const attrs = wrapper.getAttributes() as Record<string, unknown>;
  const existing = typeof attrs.style === 'string' ? attrs.style : '';
  if (/padding-left\s*:\s*2px/.test(existing) && /padding-right\s*:\s*2px/.test(existing)) {
    return;
  }
  const trimmed = existing.trim().replace(/;$/, '');
  const prefix = trimmed ? `${trimmed};` : '';
  wrapper.setAttributes({...attrs, style: `${prefix}padding-left:2px !important;padding-right:2px !important;`});
}

/**
 * Repairs forms saved before the form-background was locked in place. A user could drag the
 * card out of its signup-form slot onto the canvas root, leaving it as a top-level sibling of
 * the forms-wrapper with an empty signup-form. That broken tree breaks the public popup
 * (submit-button positioning, outside-click-close, close button). This moves an orphaned
 * card back into the empty signup-form slot.
 */
export function reattachOrphanedFormBackground(editor: Editor): void {
  const root = editor.getWrapper();
  if (!root) {
    return;
  }
  const signupForm = root.findType(POSTERMYWALL_SIGNUP_FORM_TYPE)[0];
  // A populated slot means the structure is intact — never move a card into a non-empty slot.
  if (!signupForm || signupForm.components().length > 0) {
    return;
  }
  // Match only the signup tree's card by its stable id, so a dragged-out confirmation/error
  // card (different id) is never moved into the signup slot.
  const orphan = root.components().models.find((c: Component) => c.get('type') === POSTERMYWALL_FORM_BACKGROUND_TYPE && c.getId() === SIGNUP_CONTAINER_ID);
  if (!orphan) {
    return;
  }
  root.components().remove(orphan);
  signupForm.components().add(orphan);
}

export function setFormsWrapperSignupFormId(editor: Editor, idEmailSignupForm: string): void {
  if (!idEmailSignupForm) {
    return;
  }
  const wrapper = editor.getWrapper()?.findType(POSTERMYWALL_FORMS_WRAPPER_TYPE)[0];
  if (!wrapper) {
    return;
  }
  const attrs = wrapper.getAttributes() as Record<string, unknown>;
  if (attrs['data-id-signup-form'] === idEmailSignupForm) {
    return;
  }
  wrapper.setAttributes({...attrs, 'data-id-signup-form': idEmailSignupForm});
}

function addField(editor: Editor, boxType: string): void {
  const emailForm = findSignupEmailForm(editor);
  if (emailForm) {
    emailForm.append({type: boxType});
  }
}

function findSignupEmailForm(editor: Editor): Component | null {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return null;
  }
  const emailForms = wrapper.findType(POSTERMYWALL_EMAIL_FORM_TYPE);
  if (emailForms.length === 0) {
    return null;
  }
  return emailForms[0];
}

const EMAIL_FORM_TRAILER_TYPES: ReadonlySet<string> = new Set([POSTERMYWALL_POLICY_TYPE, POSTERMYWALL_SUBMIT_BUTTON_CONTAINER_TYPE]);

/**
 * Legacy form-maker saves placed the policy paragraph and submit-button-container in the middle of the email-form's
 * child list. The React port's right-panel logic (translateRowIndexToFormChildIndex) assumes trailers live at the end,
 * so without this migration toggle-on placement and rowOrder/canvas alignment diverge. Moves both trailers to the end
 * in their relative order, no-op when they're already there.
 */
export function moveTrailersToEndOfEmailForm(editor: Editor): void {
  const emailForm = findSignupEmailForm(editor);
  if (!emailForm) {
    return;
  }
  const collection = emailForm.components();
  const trailers: Component[] = [];
  let firstTrailerIdx = -1;
  collection.models.forEach((child, idx) => {
    const childType: string = child.get('type') ?? '';
    if (EMAIL_FORM_TRAILER_TYPES.has(childType)) {
      if (firstTrailerIdx === -1) {
        firstTrailerIdx = idx;
      }
      trailers.push(child);
    }
  });
  if (firstTrailerIdx === -1) {
    return;
  }
  const hasRecognisedAfterTrailer = collection.models.slice(firstTrailerIdx + 1).some((c) => {
    const t: string = c.get('type') ?? '';
    return !EMAIL_FORM_TRAILER_TYPES.has(t) && t !== '';
  });
  if (!hasRecognisedAfterTrailer) {
    return;
  }
  trailers.forEach((trailer) => {
    collection.remove(trailer);
    collection.add(trailer, {at: collection.length});
  });
}

/**
 * Custom-field labels live in two places: state.fieldsMeta (the source of truth per the README) and the canvas
 * `<strong>` text. Legacy data can have them diverged — e.g. a rename that updated fieldsMeta without touching the
 * canvas — leaving the form preview and the right-panel out of sync. Walks every custom entry and pushes the meta
 * label onto the canvas via the existing rename helper, which is a no-op when they already agree.
 */
export function reconcileCustomFieldLabelsToCanvas(editor: Editor, fieldsMeta: readonly FieldMeta[]): void {
  fieldsMeta.forEach((meta) => {
    if (meta.component !== PMW_CUSTOM_INPUT || meta.customFieldIndex === undefined) {
      return;
    }
    updateCustomFieldLabelOnCanvas(editor, meta.customFieldIndex, meta.label);
  });
}

export function countCustomFields(fieldsMeta: FieldMeta[]): number {
  return fieldsMeta.filter((f) => f.component === PMW_CUSTOM_INPUT).length;
}

export function countCustomFieldsOnCanvas(editor: Editor): number {
  let count = 0;
  for (let i = 1; i <= MAX_CUSTOM_FIELDS; i += 1) {
    if (isFieldEnabled(editor, buildCustomBoxType(i))) {
      count += 1;
    }
  }
  return count;
}

// A toggled-off custom keeps its fieldsMeta slot, so the limit signal must union meta + canvas.
export function countCustomFieldsInRegistry(editor: Editor | null, fieldsMeta: readonly FieldMeta[]): number {
  const indices = new Set<number>();
  fieldsMeta.forEach((entry) => {
    if (entry.component === PMW_CUSTOM_INPUT && entry.customFieldIndex !== undefined) {
      indices.add(entry.customFieldIndex);
    }
  });
  if (editor) {
    for (let i = 1; i <= MAX_CUSTOM_FIELDS; i += 1) {
      if (isFieldEnabled(editor, buildCustomBoxType(i))) {
        indices.add(i);
      }
    }
  }
  return indices.size;
}

export function hasCustomFieldLimitReached(fieldsMeta: FieldMeta[]): boolean {
  return countCustomFields(fieldsMeta) >= MAX_CUSTOM_FIELDS;
}

// Returns the assigned index (1..MAX) and appends the box. fieldsMeta is not mutated; caller wires up the entry.
export function addCustomField(editor: Editor, fieldsMeta: readonly FieldMeta[]): number | null {
  const usedIndices = new Set<number>();
  fieldsMeta.forEach((entry) => {
    if (entry.component === PMW_CUSTOM_INPUT && entry.customFieldIndex !== undefined) {
      usedIndices.add(entry.customFieldIndex);
    }
  });
  for (let i = 1; i <= MAX_CUSTOM_FIELDS; i += 1) {
    if (isFieldEnabled(editor, buildCustomBoxType(i))) {
      usedIndices.add(i);
    }
  }

  if (usedIndices.size >= MAX_CUSTOM_FIELDS) {
    return null;
  }

  let newIndex = 1;
  while (usedIndices.has(newIndex) && newIndex <= MAX_CUSTOM_FIELDS) {
    newIndex += 1;
  }

  if (newIndex > MAX_CUSTOM_FIELDS) {
    return null;
  }

  // Custom-box types are pre-registered at plugin init; mid-session addType would not survive save+reload.
  const boxType = buildCustomBoxType(newIndex);

  const emailForm = findSignupEmailForm(editor);
  if (emailForm) {
    emailForm.append({type: boxType});
    return newIndex;
  }
  return null;
}

// Compound boxes (birthday, address) map to every sub-input so required toggles flip them all.
const STANDARD_BOX_TO_INPUT_TYPES: ReadonlyMap<string, readonly string[]> = new Map([
  [POSTERMYWALL_FIRST_NAME_BOX_TYPE, [POSTERMYWALL_FIRST_NAME_INPUT_TYPE]],
  [POSTERMYWALL_LAST_NAME_BOX_TYPE, [POSTERMYWALL_LAST_NAME_INPUT_TYPE]],
  [POSTERMYWALL_PHONE_BOX_TYPE, [POSTERMYWALL_PHONE_INPUT_TYPE]],
  [POSTERMYWALL_BIRTHDAY_BOX_TYPE, [POSTERMYWALL_DAY_INPUT_TYPE, POSTERMYWALL_MONTH_INPUT_TYPE, POSTERMYWALL_YEAR_INPUT_TYPE]],
  [POSTERMYWALL_ADDRESS_BOX_TYPE, [POSTERMYWALL_STREET_INPUT_TYPE, POSTERMYWALL_CITY_INPUT_TYPE, POSTERMYWALL_STATE_INPUT_TYPE, POSTERMYWALL_COUNTRY_INPUT_TYPE]],
]);

function getInputTypesForBox(boxType: string): readonly string[] {
  const standard = STANDARD_BOX_TO_INPUT_TYPES.get(boxType);
  if (standard) {
    return standard;
  }
  const customPrefix = `${POSTERMYWALL_CUSTOM_BOX_TYPE_PREFIX}-`;
  if (boxType.startsWith(customPrefix)) {
    return [`postermywall-custom-input-${boxType.slice(customPrefix.length)}`];
  }
  return [];
}

/**
 * Mutates the form-text component's content via the model so change:content fires
 * and GrapesJS re-renders the iframe DOM. The span itself is preserved across edits;
 * only its text node flips between '*' and ''.
 */
function setAsteriskForBox(editor: Editor, boxType: string, isRequired: boolean): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return;
  }
  const box = wrapper.findType(boxType)[0];
  if (!box) {
    return;
  }
  const formTextComponent = findFormTextChild(box);
  if (!formTextComponent) {
    return;
  }
  const content: unknown = formTextComponent.get('content');
  if (typeof content !== 'string') {
    return;
  }
  const updated = content.replace(
    /(class="[^"]*\bjs-asterisk-required\b[^"]*"[^>]*>)([^<]*)(<\/span>)/,
    (_match, open: string, _inner: string, close: string) => `${open}${isRequired ? '*' : ''}${close}`
  );
  if (updated !== content) {
    formTextComponent.set('content', updated);
  }
}

/**
 * Email is locked-required and is never routed through here.
 * `required` is a boolean HTML attribute — its mere presence makes the browser treat
 * the field as required, regardless of value. So we add it (empty value) when required
 * and REMOVE it entirely when not. Setting required="false" would still be required.
 */
export function updateFieldRequired(editor: Editor, boxType: string, isRequired: boolean): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return;
  }
  getInputTypesForBox(boxType).forEach((inputType) => {
    wrapper.findType(inputType).forEach((input) => {
      if (isRequired) {
        input.addAttributes({required: ''});
      } else {
        const attrs = input.getAttributes() as Record<string, unknown>;
        const {required: _required, ...rest} = attrs;
        input.setAttributes(rest);
      }
    });
  });
  setAsteriskForBox(editor, boxType, isRequired);
}

// The preference is stored on the signup-container's data-are-notifications-enabled attribute.
export function areNotificationsEnabled(editor: Editor): boolean {
  const container = findSignupContainer(editor);
  if (!container) {
    return false;
  }
  const attrs = container.getAttributes() as Record<string, unknown>;
  return attrs['data-are-notifications-enabled'] === '1';
}

export function toggleNotifications(editor: Editor): void {
  const container = findSignupContainer(editor);
  if (!container) {
    return;
  }
  const isEnabled = areNotificationsEnabled(editor);
  const newValue = isEnabled ? '0' : '1';
  container.addAttributes({'data-are-notifications-enabled': newValue});
  saveNotificationsPreference(editor, !isEnabled);
}

function findSignupContainer(editor: Editor): Component | null {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return null;
  }
  const matches = wrapper.findType(POSTERMYWALL_FORM_BACKGROUND_TYPE);
  return matches.length > 0 ? matches[0] : null;
}

export function isDescriptionEnabled(editor: Editor): boolean {
  const desc = findDescriptionComponent(editor);
  if (!desc) {
    return false;
  }
  const style = desc.getStyle() as Record<string, unknown>;
  return style.display !== 'none';
}

export function toggleDescriptionEnabled(editor: Editor): void {
  if (isDescriptionEnabled(editor)) {
    disableDescription(editor);
    return;
  }
  enableDescription(editor);
}

function findDescriptionComponent(editor: Editor): Component | null {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return null;
  }
  const matches = wrapper.findType(POSTERMYWALL_DESCRIPTION_TEXT_TYPE);
  return matches.length > 0 ? matches[0] : null;
}

function disableDescription(editor: Editor): void {
  const desc = findDescriptionComponent(editor);
  if (!desc) {
    return;
  }
  desc.addStyle({display: 'none'});
}

function enableDescription(editor: Editor): void {
  let desc = findDescriptionComponent(editor);
  if (desc) {
    desc.addStyle({display: 'block'});
  } else {
    desc = insertDescriptionComponent(editor);
  }

  if (desc) {
    editor.select(desc);
  }
}

function insertDescriptionComponent(editor: Editor): Component | null {
  const container = findSignupContainer(editor);
  if (!container) {
    return null;
  }
  const headings = container.findType(POSTERMYWALL_HEADING_TEXT_TYPE);
  const headingIndex = headings.length > 0 ? container.components().indexOf(headings[0]) : -1;
  const insertAt = headingIndex >= 0 ? headingIndex + 1 : 0;
  container.append({type: POSTERMYWALL_DESCRIPTION_TEXT_TYPE}, {at: insertAt});
  return findDescriptionComponent(editor);
}

function saveNotificationsPreference(editor: Editor, enable: boolean): void {
  const idEmailSignupForm = (editor.getModel().get(STORE_OPTS_MODEL_KEY) as {idEmailSignupForm?: string} | undefined)?.idEmailSignupForm;
  if (!idEmailSignupForm) {
    return;
  }
  void window.PMW.writeLocal(`formmaker/saveNotificationsPreference/${idEmailSignupForm}`, {
    areNotificationsEnabled: enable ? '1' : '0',
  });
}

// Matches by component string. For custom fields use updateCustomFieldLabel (matches by customFieldIndex).
export function updateFieldLabel(fieldsMeta: FieldMeta[], component: string, newLabel: string): FieldMeta[] {
  return fieldsMeta.map((f) => {
    if (f.component === component) {
      return {...f, label: newLabel};
    }
    return f;
  });
}

// All custom entries share component=PMW_CUSTOM_INPUT, so match by customFieldIndex.
export function updateCustomFieldLabel(fieldsMeta: FieldMeta[], customFieldIndex: number, newLabel: string): FieldMeta[] {
  return fieldsMeta.map((f) => {
    if (f.component === PMW_CUSTOM_INPUT && f.customFieldIndex === customFieldIndex) {
      return {...f, label: newLabel};
    }
    return f;
  });
}

export function updateCustomFieldLabelOnCanvas(editor: Editor, customFieldIndex: number, newLabel: string): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return;
  }
  const customBoxType = buildCustomBoxType(customFieldIndex);
  const boxes = wrapper.findType(customBoxType);
  if (boxes.length === 0) {
    return;
  }
  const formTextComponent = findFormTextChild(boxes[0]);
  if (formTextComponent) {
    formTextComponent.set('content', buildFormTextContent(newLabel, readAsteriskFromExistingContent(formTextComponent)));
  }
  const inputType = `postermywall-custom-input-${customFieldIndex.toString()}`;
  wrapper.findType(inputType).forEach((input) => {
    const attrs = input.getAttributes() as Record<string, unknown>;
    input.setAttributes({...attrs, customLabel: newLabel});
  });
}

function findFormTextChild(box: Component): Component | null {
  const matches = box.findType(POSTERMYWALL_FORM_TEXT_TYPE);
  return matches.length > 0 ? matches[0] : null;
}

// The asterisk char inside .js-asterisk-required is owned by the Required toggle path; preserve it across label edits.
function readAsteriskFromExistingContent(formTextComponent: Component): string {
  const content: unknown = formTextComponent.get('content');
  if (typeof content !== 'string') {
    return '';
  }
  const match = /class="js-asterisk-required"[^>]*>([^<]*)<\/span>/.exec(content);
  return match ? match[1] : '';
}

function buildFormTextContent(label: string, asteriskChar: string): string {
  return `<strong>${escapeHTML(label)}<span class="js-asterisk-required" style="${FORM_ASTERISK_STYLES}">${asteriskChar}</span></strong>`;
}

export const BIRTHDAY_INPUT_COMPONENT = 'postermywall-birthday-input';

export function updateBirthdayFormat(editor: Editor, fieldsMeta: FieldMeta[], newFormat: BirthdayFormat): FieldMeta[] {
  refreshBirthdayBoxSubFields(editor, newFormat);
  return fieldsMeta.map((f) => {
    if (f.component === BIRTHDAY_INPUT_COMPONENT) {
      return {...f, format: newFormat};
    }
    return f;
  });
}

function refreshBirthdayBoxSubFields(editor: Editor, format: BirthdayFormat): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return;
  }
  const birthdayBoxes = wrapper.findType(POSTERMYWALL_BIRTHDAY_BOX_TYPE);
  if (birthdayBoxes.length === 0) {
    return;
  }
  const container = birthdayBoxes[0].find('.pmw-birthday-box-separation-container')[0];
  if (!container) {
    return;
  }
  container.components(buildBirthdaySubFieldOrder(format));
}

function buildBirthdaySubFieldOrder(format: BirthdayFormat): ComponentDefinition[] {
  // First two fields get margin-right:10px regardless of order.
  const day: ComponentDefinition = {tagName: 'div', classes: 'day', style: {'margin-right': '10px'}, components: [{type: POSTERMYWALL_DAY_INPUT_TYPE}]};
  const month: ComponentDefinition = {tagName: 'div', classes: 'month', style: {'margin-right': '10px'}, components: [{type: POSTERMYWALL_MONTH_INPUT_TYPE}]};
  const year: ComponentDefinition = {tagName: 'div', classes: 'year', style: {}, components: [{type: POSTERMYWALL_YEAR_INPUT_TYPE}]};

  if (format.startsWith('MM')) {
    return [month, day, year];
  } else if (format.startsWith('YYYY')) {
    // Defensive fallback; BirthdayFormats currently exposes only DMY and MDY.
    return [year, month, day];
  }
  return [day, month, year];
}

export function updateCustomFieldRequired(fieldsMeta: FieldMeta[], customFieldIndex: number, isRequired: boolean): FieldMeta[] {
  return fieldsMeta.map((f) => {
    if (f.component === PMW_CUSTOM_INPUT && f.customFieldIndex === customFieldIndex) {
      return {...f, required: isRequired};
    }
    return f;
  });
}

// Compound boxes use a single logical input-name key; custom fields and email are handled separately.
const STANDARD_BOX_TO_FIELD_META_COMPONENT: ReadonlyMap<string, string> = new Map([
  [POSTERMYWALL_FIRST_NAME_BOX_TYPE, 'postermywall-first-name-input'],
  [POSTERMYWALL_LAST_NAME_BOX_TYPE, 'postermywall-last-name-input'],
  [POSTERMYWALL_PHONE_BOX_TYPE, 'postermywall-phone-input'],
  [POSTERMYWALL_BIRTHDAY_BOX_TYPE, 'postermywall-birthday-input'],
  [POSTERMYWALL_ADDRESS_BOX_TYPE, 'postermywall-address-input'],
]);

export function updateStandardFieldRequired(fieldsMeta: FieldMeta[], boxType: string, isRequired: boolean): FieldMeta[] {
  const componentKey = STANDARD_BOX_TO_FIELD_META_COMPONENT.get(boxType);
  if (componentKey === undefined) {
    return fieldsMeta;
  }
  return fieldsMeta.map((f) => {
    if (f.component === componentKey) {
      return {...f, required: isRequired};
    }
    return f;
  });
}

// Returns true only when every sub-input is required, matching the all-or-nothing checkbox UX.
export function isStandardFieldRequired(editor: Editor, boxType: string): boolean {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return false;
  }
  const inputTypes = getInputTypesForBox(boxType);
  if (inputTypes.length === 0) {
    return false;
  }
  return inputTypes.every((inputType) => {
    const inputs = wrapper.findType(inputType);
    if (inputs.length === 0) {
      return false;
    }
    return inputs.every((input) => {
      const attrs = input.getAttributes() as Record<string, unknown>;
      const value = attrs.required;
      if (typeof value === 'boolean') {
        return value;
      }
      if (typeof value === 'string') {
        return value === 'true' || value === '';
      }
      return false;
    });
  });
}

// The signup heading the error box used to inherit.
const STALE_ERROR_BOX_HEADING = 'Subscribe to our newsletter';

// Every value the body placeholder has been saved as: each locale's `pmwjs_insert_text`, plus the raw key headless renders emit.
const STALE_ERROR_BOX_MESSAGES: readonly string[] = [
  'Insert your text here',
  'pmwjs_insert_text',
  'Indsæt din tekst her',
  'Hier Text einfügen',
  'Inserte aquí su texto',
  'Insérez votre texte ici',
  'Masukkan teks Anda di sini',
  'Inserisci qui il tuo testo',
  'Voeg uw tekst hier in',
  'Wstaw swój tekst tutaj',
  'Introduza o texto aqui',
  'Вставьте сюда текст',
  'ป้อนข้อความของคุณที่นี่',
  '在此处插入文本',
];

/**
 * Error boxes seeded before the box carried its own copy inherited the signup heading and the body
 * placeholder. Only content still matching one of those is replaced, so copy an author typed is kept.
 */
export function backfillErrorBoxDefaultCopy(editor: Editor): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return;
  }
  wrapper.findType(POSTERMYWALL_ERROR_BOX_TYPE).forEach((errorBox: Component) => {
    replaceUntouchedDefaultContent({errorBox, type: POSTERMYWALL_HEADING_TEXT_TYPE, staleDefaults: [STALE_ERROR_BOX_HEADING], replacement: window.i18next.t('pmwjs_something_went_wrong')});
    replaceUntouchedDefaultContent({errorBox, type: POSTERMYWALL_FORM_TEXT_TYPE, staleDefaults: STALE_ERROR_BOX_MESSAGES, replacement: window.i18next.t('pmwjs_please_try_again')});
  });
}

interface UntouchedDefaultReplacement {
  errorBox: Component;
  type: string;
  staleDefaults: readonly string[];
  replacement: string;
}

function replaceUntouchedDefaultContent({errorBox, type, staleDefaults, replacement}: UntouchedDefaultReplacement): void {
  errorBox.findType(type).forEach((component: Component) => {
    // Rendered text, not `get('content')` — that answers the component type's default, not what the form saved.
    if (!staleDefaults.includes(component.getInnerHTML().trim())) {
      return;
    }
    // Children hold the text parsed from saved HTML; `set('content')` alone would not rewrite the output.
    component.set('content', replacement);
    component.components(replacement);
  });
}
